import { ASK_ASPER_BASE_URL } from '../config';
import type {
    Tenant,
    TenantListResponse,
    CreateTenantRequest,
    DraftInfo,
    ContentTree,
    FileContent,
    SaveFileRequest,
    SaveResponse,
    PublishResponse,
    HistoryEntry,
    DiffResponse,
    BlameEntry,
    PromptCategory,
} from '../prompts/types';

const BASE = `${ASK_ASPER_BASE_URL}prompts/tenants`;

function headers(json = true): HeadersInit {
    const h: Record<string, string> = {
        Accept: 'application/json',
        Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
    };
    const email = localStorage.getItem('email');
    if (email) h['X-User-Email'] = email;
    if (json) h['Content-Type'] = 'application/json';
    return h;
}

async function handleResponse<T>(res: Response): Promise<T> {
    if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(`${res.status} ${res.statusText}: ${body}`);
    }
    return res.json();
}

export async function listTenants(): Promise<TenantListResponse> {
    const res = await fetch(BASE, { headers: headers() });
    return handleResponse(res);
}

export async function createTenant(body: CreateTenantRequest): Promise<Tenant> {
    const res = await fetch(BASE, { method: 'POST', headers: headers(), body: JSON.stringify(body) });
    return handleResponse(res);
}

export async function openDraft(tenant: string): Promise<DraftInfo> {
    const res = await fetch(`${BASE}/${tenant}/drafts`, { method: 'POST', headers: headers() });
    return handleResponse(res);
}

export async function closeDraft(tenant: string): Promise<void> {
    const res = await fetch(`${BASE}/${tenant}/drafts`, { method: 'DELETE', headers: headers() });
    if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(`${res.status} ${res.statusText}: ${body}`);
    }
}

export async function listContent(tenant: string, branch?: string): Promise<ContentTree> {
    const qs = branch ? `?branch=${encodeURIComponent(branch)}` : '';
    const res = await fetch(`${BASE}/${tenant}/content${qs}`, { headers: headers() });
    return handleResponse(res);
}

export async function readFile(
    tenant: string,
    category: PromptCategory,
    fileId: string,
    branch?: string,
): Promise<FileContent> {
    const qs = branch ? `?branch=${encodeURIComponent(branch)}` : '';
    const res = await fetch(`${BASE}/${tenant}/${category}/${fileId}${qs}`, { headers: headers() });
    return handleResponse(res);
}

export async function saveFile(
    tenant: string,
    category: PromptCategory,
    fileId: string,
    body: SaveFileRequest,
    branch?: string,
): Promise<SaveResponse> {
    const qs = branch ? `?branch=${encodeURIComponent(branch)}` : '';
    const res = await fetch(`${BASE}/${tenant}/${category}/${fileId}${qs}`, {
        method: 'PUT',
        headers: headers(),
        body: JSON.stringify(body),
    });
    return handleResponse(res);
}

export async function publish(tenant: string): Promise<PublishResponse> {
    const res = await fetch(`${BASE}/${tenant}/publish`, { method: 'POST', headers: headers() });
    return handleResponse(res);
}

export async function createFile(
    tenant: string,
    category: PromptCategory,
    fileId: string,
    body: SaveFileRequest,
    branch?: string,
): Promise<SaveResponse> {
    const qs = branch ? `?branch=${encodeURIComponent(branch)}` : '';
    const res = await fetch(`${BASE}/${tenant}/${category}/${fileId}${qs}`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify(body),
    });
    return handleResponse(res);
}

export async function deleteFile(
    tenant: string,
    category: PromptCategory,
    fileId: string,
    branch?: string,
): Promise<SaveResponse> {
    const qs = branch ? `?branch=${encodeURIComponent(branch)}` : '';
    const res = await fetch(`${BASE}/${tenant}/${category}/${fileId}${qs}`, {
        method: 'DELETE',
        headers: headers(),
    });
    return handleResponse(res);
}

export async function branchHistory(
    tenant: string,
    branch?: string,
    limit = 20,
): Promise<HistoryEntry[]> {
    const qs = new URLSearchParams();
    if (branch) qs.set('branch', branch);
    qs.set('limit', String(limit));
    const res = await fetch(`${BASE}/${tenant}/history?${qs}`, { headers: headers() });
    return handleResponse(res);
}

export async function fileHistory(
    tenant: string,
    category: PromptCategory,
    fileId: string,
    branch?: string,
    limit = 20,
): Promise<HistoryEntry[]> {
    const qs = new URLSearchParams();
    if (branch) qs.set('branch', branch);
    qs.set('limit', String(limit));
    const res = await fetch(`${BASE}/${tenant}/${category}/${fileId}/history?${qs}`, { headers: headers() });
    return handleResponse(res);
}

export async function fileDiff(
    tenant: string,
    category: PromptCategory,
    fileId: string,
    fromSha?: string,
    toSha?: string,
): Promise<DiffResponse> {
    const qs = new URLSearchParams();
    if (fromSha) qs.set('from_sha', fromSha);
    if (toSha) qs.set('to_sha', toSha);
    const q = qs.toString();
    const res = await fetch(`${BASE}/${tenant}/${category}/${fileId}/diff${q ? `?${q}` : ''}`, { headers: headers() });
    return handleResponse(res);
}

export async function revertFile(
    tenant: string,
    category: PromptCategory,
    fileId: string,
    sha: string,
    branch?: string,
): Promise<SaveResponse> {
    const qs = new URLSearchParams({ sha });
    if (branch) qs.set('branch', branch);
    const res = await fetch(`${BASE}/${tenant}/${category}/${fileId}/revert?${qs}`, {
        method: 'POST',
        headers: headers(),
    });
    return handleResponse(res);
}

export async function fileBlame(
    tenant: string,
    category: PromptCategory,
    fileId: string,
    branch?: string,
): Promise<BlameEntry[]> {
    const qs = branch ? `?branch=${encodeURIComponent(branch)}` : '';
    const res = await fetch(`${BASE}/${tenant}/${category}/${fileId}/blame${qs}`, { headers: headers() });
    return handleResponse(res);
}
